import React, { useEffect, useState } from "react";
import { getAuthHeader } from "../../api/authToken.js";
import axios from "axios";
import "../../styles/App.css";
import {
  GENDER_LABELS,
  SKIPPED_APPLICANT_REASONS,
  SKIPPED_ROOM_REASONS,
  groupSkipped,
  softViolationLabels,
  unplacedReason,
} from "../../decision/allocationLabels.js";
import {
  generateAllocationPlan,
  applyAllocationPlan,
  getReportingPeriodPreview,
} from "../../api/dss.js";

const RUN_TYPES = [
  { value: "settlement", label: "Поселення на новий навчальний рік" },
  { value: "relocation", label: "Переселення за підсумками семестру" },
];

const TABS = [
  { key: "retained", status: "retained", label: "Залишаються на місці" },
  { key: "relocated", status: "relocated", label: "Переселяються" },
  { key: "allocated", status: "allocated", label: "Поселяються вперше" },
  { key: "unplaced", status: "unplaced", label: "Без місця" },
];

const name = (row) => row.studentName || `#${row.id}`;
const queuePlace = (row) => row.rank ?? "—";
const blockOf = (row) => row.block ?? "—";
const blockGender = (row) => (row.blockGender ? GENDER_LABELS[row.blockGender] || row.blockGender : "—");
const notes = (row) => {
  const labels = softViolationLabels(row.softViolations);
  return labels.length > 0 ? (
    <span style={{ color: "var(--full-busy)" }}>⚠ {labels.join("; ")}</span>
  ) : (
    "—"
  );
};

// Колонки таблиці кожної вкладки. «Місце в черзі» -- позиція в черзі етапу 1
// (у деградованому плані черги немає).
const COLUMNS = {
  relocated: [
    { key: "student", label: "Студент", render: name },
    { key: "from", label: "Було", render: (r) => r.previousRoomLabel ?? r.previousRoomId ?? "—" },
    { key: "to", label: "Стає", render: (r) => r.roomLabel ?? r.roomId ?? "—" },
    { key: "block", label: "Блок", render: blockOf },
    { key: "gender", label: "Стать блоку", render: blockGender },
    { key: "notes", label: "Примітки", render: notes },
    { key: "rank", label: "Місце в черзі", render: queuePlace },
  ],
  retained: [
    { key: "student", label: "Студент", render: name },
    { key: "room", label: "Кімната", render: (r) => r.roomLabel ?? r.roomId ?? "—" },
    { key: "block", label: "Блок", render: blockOf },
    { key: "gender", label: "Стать блоку", render: blockGender },
    { key: "notes", label: "Примітки", render: notes },
  ],
  allocated: [
    { key: "student", label: "Студент", render: name },
    { key: "room", label: "Кімната", render: (r) => r.roomLabel ?? r.roomId ?? "—" },
    { key: "block", label: "Блок", render: blockOf },
    { key: "gender", label: "Стать блоку", render: blockGender },
    { key: "notes", label: "Примітки", render: notes },
    { key: "rank", label: "Місце в черзі", render: queuePlace },
  ],
  unplaced: [
    { key: "student", label: "Студент", render: name },
    { key: "reason", label: "Причина", render: (r) => unplacedReason(r.reason) },
    { key: "rank", label: "Місце в черзі", render: queuePlace },
  ],
};

function applyButtonText(applying, applied) {
  if (applying) return "Застосування...";
  return applied ? "План застосовано" : "Застосувати план";
}

const AllocationPlanPage = () => {
  const API = process.env.REACT_APP_API_BASE;

  const [dormitories, setDormitories] = useState([]);
  const [dormitoryId, setDormitoryId] = useState("");
  const [runType, setRunType] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [secondPass, setSecondPass] = useState(true);

  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [planResult, setPlanResult] = useState(null);
  const [activeTab, setActiveTab] = useState("relocated");
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    const fetchDormitories = async () => {
      try {
        const res = await axios.get(`${API}/api/admin-rooms/admin-dormitories`, {
          headers: { Authorization: getAuthHeader() },
        });
        setDormitories(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchDormitories();
  }, [API]);

  const handleRunTypeChange = async (value) => {
    setRunType(value);
    if (!value) return;
    try {
      const preview = await getReportingPeriodPreview(value);
      setPeriodStart(preview.periodStart || "");
      setPeriodEnd(preview.periodEnd || "");
    } catch (err) {
      console.error("Не вдалося отримати обчислений звітний період:", err);
    }
  };

  const handleGenerate = async () => {
    if (!dormitoryId || !runType || !periodStart || !periodEnd || !effectiveDate) {
      alert(
        "Заповніть гуртожиток, тип прогону, звітний період і дату набуття чинності плану."
      );
      return;
    }
    setGenerating(true);
    setPlanResult(null);
    try {
      const result = await generateAllocationPlan({
        dormitoryId,
        runType,
        periodStart,
        periodEnd,
        effectiveDate,
        secondPass,
      });
      setPlanResult(result);
      setApplied(false);
      setActiveTab("relocated");
    } catch (err) {
      console.error(err);
      alert("Не вдалося сформувати план розподілу.");
    } finally {
      setGenerating(false);
    }
  };

  const summary = planResult?.summary || { retained: 0, relocated: 0, allocated: 0, unplaced: 0 };
  const softCount = (planResult?.plan || []).filter((item) => (item.softViolations || []).length > 0).length;
  const skippedApplicants = groupSkipped(planResult?.skipped?.applicants, SKIPPED_APPLICANT_REASONS);
  const skippedRooms = groupSkipped(planResult?.skipped?.rooms, SKIPPED_ROOM_REASONS);
  const evictionCount = (planResult?.evictionCandidates || []).length;
  const rows = (planResult?.plan || []).filter(
    (item) => item.status === TABS.find((t) => t.key === activeTab).status
  );

  const handleApplyClick = () => {
    if (!planResult) return;
    const realMoves = summary.relocated ?? 0;
    const confirmed = window.confirm(
      `Застосувати цей план розподілу? Кількість реальних переїздів: ${realMoves}.`
    );
    if (!confirmed) return;
    handleApply();
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await applyAllocationPlan(planResult.runId);
      setApplied(true);
      alert(`План застосовано. Виконано змін: ${res.applied ?? summary.relocated + summary.allocated}.`);
    } catch (err) {
      console.error(err);
      // 404/409: зрозуміла відповідь ядра (план застарів, уже застосовано).
      if (err?.status === 404 || err?.status === 409) {
        alert(`Не вдалося застосувати план: ${err.message}`);
      } else {
        alert("Не вдалося застосувати план.");
      }
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="page-container">
      <h3 className="text-header">Формування плану розподілу місць</h3>

      <details className="input-component" data-testid="plan-help">
        <summary className="text-header" style={{ cursor: "pointer" }}>
          Як працює план розподілу
        </summary>
        <ol className="text-header">
          <li>
            <strong>Хто потрапляє.</strong> «Переселення» — мешканці вибраного гуртожитку з позначкою «потребує
            переселення»; «Поселення на новий рік» — усі студенти без кімнати. Студенти без статі, курсу чи
            віддаленості не рахуються (показано в «Не потрапили в розрахунок»).
          </li>
          <li>
            <strong>Звітний період.</strong> Бали й порушення беруться лише з подій у межах вказаних дат (типово з 1
            вересня).
          </li>
          <li>
            <strong>Етап 1 — черга.</strong> Спершу пільга (3 → 2 → 1), у межах пільги — показник близькості C (AHP +
            TOPSIS за сесією 30–50, активністю в гуртожитку, діяльністю й віддаленістю); за рівного C — більший бал за
            сесію, далі за діяльність. Студенти без сесії (або з балом нижче 30) — окрема група після загального
            конкурсу.
          </li>
          <li>
            <strong>Етап 2 — кімната за чергою.</strong> Жорсткі обмеження: вільне місце, стать блоку (порожній блок
            бере стать першого поселеного), доступність за потреби. М'яке: не поселяти 1 курс разом із 4+. Мешканець,
            чия кімната допустима, залишається. Порядок вибору: поточна кімната → непорожній блок → менше вільних
            місць → номер. Якщо м'якого обмеження дотриматись не можна — кімната з позначкою «порушення м'якого
            обмеження».
          </li>
          <li>
            <strong>Друга ітерація</strong> — ще один прохід лише для тих, кому не знайшлося місця.
          </li>
          <li>
            <strong>Вкладки:</strong> залишаються на місці / переселяються / поселяються вперше / без місця (з
            причиною).
          </li>
          <li>
            <strong>Кандидати на виселення</strong> — 3 і більше порушень за період; це лише позначка для
            відповідальної особи.
          </li>
          <li>
            <strong>Дата набуття чинності</strong> — дата, якою записуються переселення.
          </li>
          <li>
            <strong>«Застосувати план»</strong> — сервер бере збережений план, перевіряє, що ніхто не змінив кімнату
            після формування (інакше «план застарів»), і записує переселення та нові кімнати; повторно застосувати
            той самий план не можна. Остаточне рішення — за відповідальною особою.
          </li>
        </ol>
      </details>

      <div className="header-content">
        <select
          className="header-content-input"
          value={dormitoryId}
          onChange={(e) => setDormitoryId(e.target.value)}
        >
          <option value="">Оберіть гуртожиток</option>
          {dormitories.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          className="header-content-input"
          value={runType}
          onChange={(e) => handleRunTypeChange(e.target.value)}
        >
          <option value="">Оберіть тип прогону</option>
          {RUN_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      {runType && (
        <div className="input-component">
          <p className="text-header">
            Ураховуються події з {periodStart || "..."} по {periodEnd || "..."}
          </p>
          <div className="form-container">
            <div className="input-block text-header">
              <p className="text-header">Початок звітного періоду:</p>
              <input
                type="date"
                className="input-component-with-border"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="input-block text-header">
              <p className="text-header">Кінець звітного періоду:</p>
              <input
                type="date"
                className="input-component-with-border"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="form-container">
            <div className="input-block text-header">
              <p className="text-header">Дата набуття чинності плану:</p>
              <input
                type="date"
                className="input-component-with-border"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="input-block text-header">
              <label className="text-header">
                <input
                  type="checkbox"
                  checked={secondPass}
                  onChange={(e) => setSecondPass(e.target.checked)}
                />{" "}
                Друга ітерація для непоселених
              </label>
            </div>
          </div>

          <button
            className="submit-button"
            disabled={generating}
            onClick={handleGenerate}
          >
            {generating ? "Формування плану..." : "Сформувати план"}
          </button>
          {generating && (
            <p className="text-header">
              Перший запит після простою сервера може тривати до 60 секунд, будь
              ласка, зачекайте.
            </p>
          )}
        </div>
      )}

      {planResult && (
        <>
          {planResult.degraded && (
            <p className="badge" style={{ backgroundColor: "var(--half-busy)", color: "#000" }}>
              ⚠ Розрахунок виконано в деградованому режимі (degraded)
            </p>
          )}

          <div className="input-component" data-testid="plan-summary">
            <div className="form-container">
              <p className="input-block text-header">
                <strong>Усього в плані:</strong> {(planResult.plan || []).length}
              </p>
              <p className="input-block text-header">
                <strong>З порушенням м'якого обмеження:</strong> {softCount}
              </p>
              <p className="input-block text-header">
                <strong>Кандидатів на виселення (3+ порушення):</strong> {evictionCount}
              </p>
            </div>
            {skippedApplicants.length > 0 && (
              <p className="text-header" data-testid="plan-skipped-applicants">
                Не потрапили в розрахунок: {skippedApplicants.map((s) => `${s.label} — ${s.count}`).join("; ")}.
              </p>
            )}
            {skippedRooms.length > 0 && (
              <p className="text-header" data-testid="plan-skipped-rooms">
                Не враховано кімнат: {skippedRooms.map((s) => `${s.label} — ${s.count}`).join("; ")}.
              </p>
            )}
          </div>

          <div className="tabs-nav">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={`tab-button${activeTab === tab.key ? " active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label} ({summary[tab.status] ?? 0})
              </button>
            ))}
          </div>

          <table className="table">
            <thead>
              <tr>
                {COLUMNS[activeTab].map((column) => (
                  <th key={column.key} className="table-header">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    {COLUMNS[activeTab].map((column) => (
                      <td key={column.key} className="table-content">
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="table-content" colSpan={COLUMNS[activeTab].length}>
                    Немає студентів у цій категорії.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <button
            className="restore-button"
            style={{ width: "50%", marginLeft: 0, marginTop: "12px" }}
            disabled={applying || applied}
            onClick={handleApplyClick}
          >
            {applyButtonText(applying, applied)}
          </button>
        </>
      )}
    </div>
  );
};

export default AllocationPlanPage;
